import React, { useState, useEffect } from "react";
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle2, Loader2 } from 'lucide-react';
import AuthLayout from "../layouts/AuthLayout";
import { userService } from "../../_services";
import * as z from 'zod';
import Signupleft from "../../images/signupleft.webp";
import signupcheck from "../../images/savecheck.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

const setUsernameSchema = z.object({
    Fname: z.string().min(3, 'First Name must be at least 3 characters long'),
    Lname: z.string().min(2, 'Last Name must be at least 2 characters long'),
    Phone: z.string()
        .refine(val => val.replace(/[- )(]/g, '').length === 10, 'Phone Number must be 10 digits')
        .refine(val => userService.ValidPhone(val), 'Please enter valid Phone Number'),
    Email: z.string()
        .min(1, 'Enter your email')
        .refine(val => userService.ValidEmailRegex(val), 'Invalid Email'),
    UserName: z.string()
        .refine(val => val.trim().length >= 5, 'Username must be at least 5 characters long')
        .refine(val => !val.trim().includes(' '), 'Username does not allow space')
        .refine(val => !/^[0-9]+$/.test(val.trim()), 'This Username not allowed')
        .refine(
            val => !['user', 'rxh', 'temp'].some(kw => val.trim().includes(kw)),
            'This Username not allowed'
        ),
    Password: z.string().min(5, 'Password must be at least 5 characters long'),
    ConfirmPassword: z.string(),
}).refine(data => data.Password === data.ConfirmPassword, {
    message: 'Password & Confirm Password must match',
    path: ['ConfirmPassword'],
});

export default function SetUsername() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [formData, setFormData] = useState({
        Fname: '',
        Lname: '',
        Phone: '',
        Email: '',
        UserName: '',
        Password: '',
        ConfirmPassword: '',
    });
    const [dataValidated, setDataValidated] = useState(false);
    const [loadingText, setLoadingText] = useState('Please wait while your request is processing');
    const [userId, setUserId] = useState('');
    const [accessCode, setAccessCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [userUpdated, setUserUpdated] = useState(false);
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const token = searchParams.get('accesscode');
        const username = searchParams.get('username');
        setAccessCode(token);
        verifyUser(token, username);
    }, [searchParams]);

    const verifyUser = async (token, username) => {
        try {
            const apidata = await userService.verifytuser(token, username);
            if (apidata.length > 0 && apidata[0].statusMessage === "Success") {
                const value = apidata[0];
                setFormData({
                    ...formData,
                    Fname: value.fname || '',
                    Lname: value.lname || '',
                    Phone: value.phone || '',
                    Email: value.email || '',
                });
                setUserId(value.userId || '');
                setDataValidated(true);
            } else {
                setDataValidated(false);
                setLoadingText("Invalid Request! Please Click below to signup");
            }
        } catch (error) {
            console.error('Error verifying user:', error);
            setDataValidated(false);
            setLoadingText("Invalid Request! Please Click below to signup");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Validate name fields (only letters)
        if (name === "Fname" || name === "Lname") {
            const special = /^[a-zA-Z]+$/;
            if (value !== "" && !value.match(special)) {
                return;
            }
        }
        
        setFormData({ ...formData, [name]: value });
        // Clear error when typing
        setErrors({ ...errors, [name]: '' });
    };

    const validateForm = async () => {
        // Zod sync validation
        const result = setUsernameSchema.safeParse(formData);
        const fe = result.success ? {} : result.error.flatten().fieldErrors;
        const newErrors = {
            ...(fe.Fname?.[0] && { Fname: fe.Fname[0] }),
            ...(fe.Lname?.[0] && { Lname: fe.Lname[0] }),
            ...(fe.Phone?.[0] && { Phone: fe.Phone[0] }),
            ...(fe.Email?.[0] && { Email: fe.Email[0] }),
            ...(fe.UserName?.[0] && { UserName: fe.UserName[0] }),
            ...(fe.Password?.[0] && { Password: fe.Password[0] }),
            ...(fe.ConfirmPassword?.[0] && { ConfirmPassword: fe.ConfirmPassword[0] }),
        };

        // Async API checks (unchanged — run regardless of sync result to match original behaviour)
        const trimmedUsername = formData.UserName.trim();
        try {
            const usernameCheck = await userService.GetUserNameFilter(trimmedUsername);
            if (usernameCheck.length > 0 && usernameCheck[0].code !== '0') {
                newErrors.UserName = usernameCheck[0].message;
            }
        } catch (error) {
            console.error('Username validation error:', error);
        }

        try {
            const phoneCheck = await userService.GetPhoneNoValidate(formData.Phone);
            if (phoneCheck[0].code !== "1") {
                newErrors.Phone = 'Invalid Phone Number';
            } else if (phoneCheck[0].message) {
                setFormData({ ...formData, Phone: phoneCheck[0].message });
            }
        } catch (error) {
            console.error('Phone validation error:', error);
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!agreeTerms) return;

        const validationErrors = await validateForm();
        
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsLoading(true);
        
        try {
            const apidata = await userService.updatetuser(
                userId,
                formData.UserName.trim(),
                accessCode,
                formData.Fname,
                formData.Lname,
                formData.Email,
                formData.Phone,
                formData.Password
            );

            if (apidata.length > 0) {
                if (apidata[0].code !== '-1') {
                    setUserUpdated(true);
                } else {
                    const newErrors = {};
                    if (apidata[0].statusMessage === 'Phone no is already exist') {
                        newErrors.Phone = 'Phone Number already exists';
                        setFormData({ ...formData, Phone: '' });
                    } else {
                        newErrors.UserName = apidata[0].statusMessage;
                        setFormData({ ...formData, UserName: '' });
                    }
                    setErrors(newErrors);
                }
            }
        } catch (error) {
            console.error('Update error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Check if user is already logged in
    if (!userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate to="/Affiliate_Url" replace />;
    }

    // Success screen
    if (userUpdated) {
        return (
            <AuthLayout imageSrc={Signupleft}>
                <Card className="border-0 shadow-none">
                    <CardContent className="pt-6">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <img src={signupcheck} alt="Success" className="w-20 h-20" />
                            <p className="text-lg font-semibold">Updated successfully</p>
                            <Button onClick={() => navigate('/login')} className="w-full">
                                Sign In Now
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </AuthLayout>
        );
    }

    // Loading/Invalid screen
    if (!dataValidated) {
        return (
            <AuthLayout imageSrc={Signupleft}>
                <Card className="border-0 shadow-none">
                    <CardContent className="pt-6">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <p className="text-lg">{loadingText}</p>
                            {loadingText !== "Please wait while your request is processing" && (
                                <Button onClick={() => navigate('/signup')} className="w-full">
                                    Sign Up here
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </AuthLayout>
        );
    }

    // Main form
    return (
        <AuthLayout imageSrc={Signupleft}>
            <Card className="border-0 shadow-none">
                <CardHeader>
                    <CardTitle>Set Details</CardTitle>
                    <CardDescription>Complete your account setup</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* First Name */}
                        <div className="space-y-2">
                            <Label htmlFor="Fname">First Name</Label>
                            <Input
                                id="Fname"
                                name="Fname"
                                value={formData.Fname}
                                onChange={handleInputChange}
                                className={cn(errors.Fname && "border-destructive")}
                            />
                            {errors.Fname && (
                                <p className="text-sm text-destructive">{errors.Fname}</p>
                            )}
                        </div>

                        {/* Last Name */}
                        <div className="space-y-2">
                            <Label htmlFor="Lname">Last Name</Label>
                            <Input
                                id="Lname"
                                name="Lname"
                                value={formData.Lname}
                                onChange={handleInputChange}
                                className={cn(errors.Lname && "border-destructive")}
                            />
                            {errors.Lname && (
                                <p className="text-sm text-destructive">{errors.Lname}</p>
                            )}
                        </div>

                        {/* Phone */}
                        <div className="space-y-2">
                            <Label htmlFor="Phone">Phone Number</Label>
                            <Input
                                id="Phone"
                                name="Phone"
                                value={formData.Phone}
                                onChange={handleInputChange}
                                className={cn(errors.Phone && "border-destructive")}
                            />
                            {errors.Phone && (
                                <p className="text-sm text-destructive">{errors.Phone}</p>
                            )}
                        </div>

                        {/* Email */}
                        <div className="space-y-2">
                            <Label htmlFor="Email">Email</Label>
                            <Input
                                id="Email"
                                name="Email"
                                type="email"
                                value={formData.Email}
                                onChange={handleInputChange}
                                className={cn(errors.Email && "border-destructive")}
                            />
                            {errors.Email && (
                                <p className="text-sm text-destructive">{errors.Email}</p>
                            )}
                        </div>

                        {/* Username */}
                        <div className="space-y-2">
                            <Label htmlFor="UserName">Username</Label>
                            <Input
                                id="UserName"
                                name="UserName"
                                value={formData.UserName}
                                onChange={handleInputChange}
                                className={cn(errors.UserName && "border-destructive")}
                            />
                            {errors.UserName && (
                                <p className="text-sm text-destructive">{errors.UserName}</p>
                            )}
                        </div>

                        {/* Password */}
                        <div className="space-y-2">
                            <Label htmlFor="Password">Password</Label>
                            <div className="relative">
                                <Input
                                    id="Password"
                                    name="Password"
                                    type={showPassword ? "text" : "password"}
                                    value={formData.Password}
                                    onChange={handleInputChange}
                                    maxLength={30}
                                    className={cn(errors.Password && "border-destructive", "pr-10")}
                                />
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                            {errors.Password && (
                                <p className="text-sm text-destructive">{errors.Password}</p>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div className="space-y-2">
                            <Label htmlFor="ConfirmPassword">Confirm Password</Label>
                            <div className="relative">
                                <Input
                                    id="ConfirmPassword"
                                    name="ConfirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={formData.ConfirmPassword}
                                    onChange={handleInputChange}
                                    maxLength={30}
                                    className={cn(errors.ConfirmPassword && "border-destructive", "pr-10")}
                                />
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground"
                                >
                                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                            {errors.ConfirmPassword && (
                                <p className="text-sm text-destructive">{errors.ConfirmPassword}</p>
                            )}
                        </div>

                        {/* Terms Checkbox */}
                        <div className="flex items-start space-x-2">
                            <Checkbox
                                id="terms"
                                checked={agreeTerms}
                                onCheckedChange={setAgreeTerms}
                            />
                            <label
                                htmlFor="terms"
                                className="text-sm leading-tight cursor-pointer"
                            >
                                By clicking Update & Login you agree to our{' '}
                                <a href="/terms/" target="_blank" rel="noreferrer" className="text-primary hover:no-underline hover:text-foreground">
                                    Terms and Conditions
                                </a>
                                .
                            </label>
                        </div>

                        {/* Submit Button */}
                        <Button
                            type="submit"
                            className="w-full"
                            disabled={!agreeTerms || isLoading}
                        >
                            {isLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Processing</> : 'Update & Login'}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </AuthLayout>
    );
}
